import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { authHeader } from "@/lib/token";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import AuthModal from "@/components/AuthModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import SiteFooter from "@/components/SiteFooter";
import {
  Plus, Send, Trash2, LogIn, Ticket, Loader2, CheckCircle, Clock, ChevronLeft, Shield, User,
  ChevronRight, Activity, BookOpen, Sparkles, MessageSquare, X, Headphones
} from "lucide-react";

const StatusBadge = ({ status }) => (
  status === "resolved" ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-0.5 text-[11px] font-medium text-emerald-300 ring-1 ring-inset ring-emerald-400/20">
      <CheckCircle className="h-3 w-3" />Résolu
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2.5 py-0.5 text-[11px] font-medium text-amber-300 ring-1 ring-inset ring-amber-400/20">
      <Clock className="h-3 w-3" />Ouvert
    </span>
  )
);

const FIELD = "w-full rounded-xl border border-white/[0.08] bg-white/[0.03] px-3.5 text-sm text-white placeholder:text-white/25 outline-none transition focus:border-blue-500/60 focus:bg-white/[0.05] focus:shadow-[0_0_0_4px_rgba(59,130,246,0.12)]";

const fmtDate = (d) => new Date(d).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

// Raccourcis d'aide affichés au-dessus des tickets.
const HELP = [
  { to: "/tutorial", icon: BookOpen, title: "Créer son bot Discord", text: "Le guide pas à pas", tint: "bg-amber-500/15 text-amber-300" },
  { to: "/status", icon: Activity, title: "Statut des services", text: "Vérifier un incident", tint: "bg-emerald-500/15 text-emerald-300" },
  { to: "/comparer", icon: Sparkles, title: "Choisir un produit", text: "Comparer les offres", tint: "bg-violet-500/15 text-violet-300" },
];

export default function SupportPage() {
  const { user } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [tickets, setTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [productId, setProductId] = useState("");
  const [products, setProducts] = useState([]);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const hdr = useCallback(() => ({ ...authHeader() }), []);

  const fetchTickets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/support/tickets`, { headers: hdr() });
      setTickets(res.data || []);
      return res.data || [];
    } catch { return []; }
    finally { setLoading(false); }
  }, [hdr]);

  useEffect(() => {
    if (user) fetchTickets();
    else setLoading(false);
  }, [user, fetchTickets]);

  // Produits pour le ticket : ceux du client d'abord.
  useEffect(() => {
    if (!user) return;
    Promise.all([
      axios.get(`${API}/shop/bots`).catch(() => ({ data: [] })),
      axios.get(`${API}/bots`, { headers: hdr() }).catch(() => ({ data: [] })),
    ]).then(([shop, mine]) => {
      const owned = new Set((mine.data || []).map(b => b.bot_type));
      const list = (shop.data || []).map(p => ({ id: p.id, name: p.name, owned: owned.has(p.id) }));
      list.sort((a, b) => Number(b.owned) - Number(a.owned));
      setProducts(list);
    });
  }, [user, hdr]);

  const createTicket = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Sujet et message requis"); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/support/tickets`, { subject, message, product_id: productId || null }, { headers: hdr() });
      toast.success("Ticket créé ! Notre équipe vous répondra rapidement.");
      setSubject(""); setMessage(""); setProductId(""); setShowForm(false);
      setTickets(prev => [res.data.ticket, ...prev]);
      setActiveTicket(res.data.ticket);
    } catch { toast.error("Erreur de création"); }
    finally { setSending(false); }
  };

  const deleteTicket = async (ticketId, e) => {
    e?.stopPropagation();
    try {
      await axios.delete(`${API}/support/tickets/${ticketId}`, { headers: hdr() });
      setTickets(prev => prev.filter(t => t.id !== ticketId));
      if (activeTicket?.id === ticketId) setActiveTicket(null);
      toast.success("Ticket supprimé");
    } catch { toast.error("Erreur"); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !activeTicket) return;
    setSending(true);
    try {
      await axios.post(`${API}/support/tickets/${activeTicket.id}/reply`, { message: reply }, { headers: hdr() });
      setReply("");
      const fresh = await fetchTickets();
      const updated = fresh.find(t => t.id === activeTicket.id);
      if (updated) setActiveTicket(updated);
    } catch { toast.error("Erreur d'envoi"); }
    finally { setSending(false); }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-vanta-base flex flex-col" data-testid="support-page">
        <Navbar onAuthClick={() => setShowAuth(true)} />
        <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} mode={authMode} setMode={setAuthMode} />
        <div className="relative flex flex-1 items-center justify-center px-5 py-28">
          <div className="pointer-events-none absolute left-1/2 top-1/3 h-80 w-[640px] -translate-x-1/2 rounded-full bg-gradient-to-r from-blue-600/20 to-violet-600/20 blur-[90px]" />
          <div className="page-rise relative w-full max-w-md rounded-3xl border border-white/[0.08] bg-[#0f1019]/90 p-8 text-center backdrop-blur">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-violet-600 shadow-lg shadow-blue-900/40">
              <Headphones className="h-7 w-7 text-white" />
            </span>
            <h1 className="mt-5 font-heading text-2xl font-bold">Support VantaPulse</h1>
            <p className="mt-2 text-sm text-white/45">Connecte-toi pour ouvrir un ticket et suivre les réponses de l'équipe.</p>
            <Button data-testid="support-login-btn" onClick={() => setShowAuth(true)} className="mt-6 h-11 w-full rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 text-sm font-semibold text-white hover:brightness-110">
              <LogIn className="mr-2 h-4 w-4" />Se connecter
            </Button>
          </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const openCount = tickets.filter((t) => t.status !== "resolved").length;

  return (
    <div className="min-h-screen bg-vanta-base" data-testid="support-page">
      <Navbar onAuthClick={() => setShowAuth(true)} />
      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} mode={authMode} setMode={setAuthMode} />

      <div className="pointer-events-none absolute inset-x-0 top-0 h-80 overflow-hidden">
        <div className="absolute left-1/2 top-[-180px] h-[360px] w-[760px] -translate-x-1/2 rounded-full bg-gradient-to-r from-cyan-500/15 via-blue-600/15 to-violet-600/15 blur-[90px]" />
      </div>

      <div className="relative mx-auto max-w-4xl px-4 pb-16 pt-28 sm:px-6">
        {!activeTicket ? (
          <>
            <div className="page-rise flex flex-wrap items-end justify-between gap-4">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-white/65">
                  <Headphones className="h-3.5 w-3.5 text-cyan-300" />Centre d'aide
                </span>
                <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Comment pouvons-nous t'aider ?</h1>
                <p className="mt-2 text-sm text-white/45">Ouvre un ticket, notre équipe te répond rapidement.</p>
              </div>
              <Button data-testid="new-ticket-btn" onClick={() => setShowForm(!showForm)} className="h-11 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 px-5 font-semibold text-white shadow-[0_10px_30px_-10px_rgba(79,70,229,0.8)] hover:brightness-110">
                {showForm ? <X className="mr-1.5 h-4 w-4" /> : <Plus className="mr-1.5 h-4 w-4" />}{showForm ? "Annuler" : "Nouveau ticket"}
              </Button>
            </div>

            <div className="page-rise mt-8 grid gap-3 sm:grid-cols-3" style={{ animationDelay: "60ms" }}>
              {HELP.map(({ to, icon: Icon, title, text, tint }) => (
                <Link key={to} to={to} className="group flex items-center gap-3 rounded-2xl border border-white/[0.07] bg-white/[0.02] p-4 transition hover:border-white/15 hover:bg-white/[0.04]">
                  <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tint}`}><Icon className="h-5 w-5" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium">{title}</span>
                    <span className="block text-xs text-white/40">{text}</span>
                  </span>
                  <ChevronRight className="h-4 w-4 text-white/20 transition group-hover:translate-x-0.5 group-hover:text-white/50" />
                </Link>
              ))}
            </div>

            {showForm && (
              <div className="page-rise mt-6 rounded-2xl border border-blue-500/20 bg-[#0f1019] p-6 shadow-[0_20px_60px_-30px_rgba(59,130,246,0.5)]" data-testid="ticket-form">
                <div className="mb-5 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500/15 text-blue-300"><MessageSquare className="h-5 w-5" /></span>
                  <div>
                    <p className="font-semibold">Nouveau ticket</p>
                    <p className="text-xs text-white/40">Plus ta description est précise, plus la réponse est rapide.</p>
                  </div>
                </div>
                {products.length > 0 && (
                  <>
                    <Label className="mb-1.5 block text-[13px] font-medium text-white/75">Produit concerné</Label>
                    <select data-testid="ticket-product-select" value={productId} onChange={(e) => setProductId(e.target.value)} className={`${FIELD} mb-4 h-11 [&>option]:bg-[#0f1019]`}>
                      <option value="">Autre / question générale</option>
                      {products.map(p => <option key={p.id} value={p.id}>{p.name}{p.owned ? " (acheté)" : ""}</option>)}
                    </select>
                  </>
                )}
                <Label className="mb-1.5 block text-[13px] font-medium text-white/75">Sujet</Label>
                <Input data-testid="ticket-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Ex : Problème avec mon produit" className={`${FIELD} mb-4 h-11 focus-visible:ring-0 focus-visible:ring-offset-0`} />
                <Label className="mb-1.5 block text-[13px] font-medium text-white/75">Message</Label>
                <Textarea data-testid="ticket-message-input" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Décris ton problème en détail…" rows={5} className={`${FIELD} mb-5 py-3 focus-visible:ring-0 focus-visible:ring-offset-0`} />
                <div className="flex justify-end">
                  <Button data-testid="ticket-submit-btn" onClick={createTicket} disabled={sending} className="h-11 rounded-xl bg-blue-600 px-5 font-semibold hover:bg-blue-500">
                    {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}Envoyer le ticket
                  </Button>
                </div>
              </div>
            )}

            <div className="page-rise mt-10 mb-3 flex items-center justify-between" style={{ animationDelay: "100ms" }}>
              <h2 className="font-heading text-lg font-bold">Mes tickets</h2>
              {tickets.length > 0 && <span className="text-xs text-white/40">{openCount} ouvert{openCount > 1 ? "s" : ""} · {tickets.length} au total</span>}
            </div>

            {loading ? (
              <div className="space-y-3">{[0, 1].map((n) => <div key={n} className="h-[72px] animate-pulse rounded-2xl bg-white/[0.03]" />)}</div>
            ) : tickets.length === 0 ? (
              <div className="page-rise rounded-2xl border border-dashed border-white/10 py-14 text-center" style={{ animationDelay: "120ms" }}>
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-white/[0.04]"><Ticket className="h-6 w-6 text-white/25" /></span>
                <p className="mt-4 text-sm font-medium text-white/70">Aucun ticket pour le moment</p>
                <p className="mt-1 text-xs text-white/35">Tu peux aussi poser ta question à l'assistant IA, en bas à droite.</p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {tickets.map((t, index) => (
                  <div key={t.id} data-testid={`ticket-item-${t.id}`} onClick={() => setActiveTicket(t)}
                    className="page-rise group flex cursor-pointer items-center gap-4 rounded-2xl border border-white/[0.07] bg-[#0f1019] p-4 transition-all hover:-translate-y-0.5 hover:border-white/15"
                    style={{ animationDelay: `${120 + index * 40}ms` }}>
                    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${t.status === "resolved" ? "bg-emerald-500/10 text-emerald-300" : "bg-blue-500/10 text-blue-300"}`}>
                      <Ticket className="h-5 w-5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-2 truncate text-sm font-medium text-white/90">
                        <span className="truncate">{t.subject}</span>
                        {t.product_name && <span className="shrink-0 rounded-md bg-blue-500/10 px-1.5 py-0.5 text-[11px] text-blue-300">{t.product_name}</span>}
                      </p>
                      <p className="mt-0.5 truncate text-xs text-white/35">{t.message}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      {(t.replies?.length || 0) > 0 && <span className="hidden items-center gap-1 text-[11px] text-white/40 sm:flex"><MessageSquare className="h-3.5 w-3.5" />{t.replies.length}</span>}
                      <StatusBadge status={t.status} />
                      <button onClick={(e) => deleteTicket(t.id, e)} aria-label="Supprimer le ticket" className="rounded-lg p-1.5 text-red-400/70 opacity-0 transition hover:bg-red-500/10 hover:text-red-300 group-hover:opacity-100" data-testid={`delete-ticket-${t.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <ChevronRight className="h-4 w-4 text-white/20" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          /* Fil du ticket */
          <div data-testid="ticket-thread" className="page-rise">
            <button onClick={() => setActiveTicket(null)} className="mb-5 flex items-center gap-1.5 text-sm text-white/45 transition-colors hover:text-white" data-testid="back-to-tickets-btn">
              <ChevronLeft className="h-4 w-4" />Retour aux tickets
            </button>
            <div className="overflow-hidden rounded-3xl border border-white/[0.08] bg-[#0f1019]">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/[0.06] bg-white/[0.02] px-5 py-4 sm:px-6">
                <div className="min-w-0">
                  <h1 className="truncate font-heading text-lg font-bold">{activeTicket.subject}</h1>
                  <p className="mt-0.5 text-xs text-white/40">
                    Ouvert le {fmtDate(activeTicket.created_at)}{activeTicket.product_name ? ` · ${activeTicket.product_name}` : ""}
                  </p>
                </div>
                <StatusBadge status={activeTicket.status} />
              </div>

              <div className="space-y-4 px-4 py-6 sm:px-6">
                <div className="flex items-end justify-end gap-2">
                  <div className="max-w-[80%] rounded-2xl rounded-br-md bg-gradient-to-br from-blue-600 to-indigo-600 px-4 py-3 shadow-md shadow-blue-950/40">
                    <p className="whitespace-pre-wrap text-sm text-white">{activeTicket.message}</p>
                    <p className="mt-1.5 text-[10.5px] text-white/55">Vous · {fmtDate(activeTicket.created_at)}</p>
                  </div>
                </div>
                {(activeTicket.replies || []).map((r, i) => (
                  r.from === "client" ? (
                    <div key={`${activeTicket.id}-r${i}`} className="flex items-end justify-end gap-2">
                      <div className="max-w-[80%] rounded-2xl rounded-br-md bg-gradient-to-br from-blue-600 to-indigo-600 px-4 py-3 shadow-md shadow-blue-950/40">
                        {r.image_url && <img src={r.image_url} alt="" className="mb-2 max-h-48 rounded-lg border border-white/10" />}
                        <p className="whitespace-pre-wrap text-sm text-white">{r.message}</p>
                        <p className="mt-1.5 flex items-center gap-1 text-[10.5px] text-white/55"><User className="h-3 w-3" />Vous · {fmtDate(r.created_at)}</p>
                      </div>
                    </div>
                  ) : (
                    <div key={`${activeTicket.id}-r${i}`} className="flex items-end gap-2">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-violet-600 p-[1.5px]">
                        <img src="/images/logo-navbar.png" alt="" className="h-full w-full rounded-full bg-[#0b1020] object-cover" />
                      </span>
                      <div className="max-w-[80%] rounded-2xl rounded-bl-md border border-white/[0.07] bg-white/[0.05] px-4 py-3">
                        <p className="mb-1 flex items-center gap-1 text-[11px] font-medium text-blue-300"><Shield className="h-3 w-3" />Équipe VantaPulse</p>
                        {r.image_url && <img src={r.image_url} alt="" className="mb-2 max-h-48 rounded-lg border border-white/10" />}
                        <p className="whitespace-pre-wrap text-sm text-white/90">{r.message}</p>
                        <p className="mt-1.5 text-[10.5px] text-white/30">{fmtDate(r.created_at)}</p>
                      </div>
                    </div>
                  )
                ))}
              </div>

              <div className="border-t border-white/[0.06] p-3 sm:p-4">
                <div className="flex items-end gap-2 rounded-2xl border border-white/[0.08] bg-white/[0.03] py-1.5 pl-4 pr-1.5 transition focus-within:border-blue-500/50 focus-within:shadow-[0_0_0_4px_rgba(59,130,246,0.1)]">
                  <textarea
                    data-testid="ticket-reply-input"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                    placeholder="Répondre au ticket…"
                    rows={1}
                    className="max-h-32 flex-1 resize-none border-none bg-transparent py-2 text-sm text-white/90 outline-none placeholder:text-white/25"
                    style={{ minHeight: "24px", boxShadow: "none" }}
                  />
                  <button data-testid="ticket-reply-send-btn" onClick={sendReply} disabled={!reply.trim() || sending}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 transition hover:brightness-110 disabled:from-white/10 disabled:to-white/10" aria-label="Envoyer">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin text-white" /> : <Send className="h-4 w-4 text-white" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
